import cx from 'classnames'
import { EMAIL_REGEX_STR } from 'common/Prose/utils/link'
import { SelectionAttributes } from 'proseShared/plugins/MentionPrompt/MentionPrompt'
import { MentionPrompt } from 'proseShared/prompts/MentionPrompt/MentionPrompt'
import { useState } from 'react'
import { getArticleUrl, getFolderUrl } from 'utils/urlUtils'
import * as validator from 'validator'
import Styles from 'common/LinkModal/LinkModal.module.scss'
import { QueryOrLink } from 'common/LinkModal/QueryOrLink'
import { Button } from 'blaze-ui/Button'
import { Unlink } from 'almanac-ui/icons'
import * as React from 'react'

const EMAIL_REGEX = new RegExp(`^mailto:${EMAIL_REGEX_STR}$`)

const checkUrl = (str: string) => validator.isURL(str) || EMAIL_REGEX.test(str)

export interface Props {
  text: string
  initialQuery?: string
  onLinked: ({ link, text }: { link?: string; text: string }) => void
  onClose: () => void
  onLinkHeading: () => void
  onChange: (text: string) => void
  onRemoveLink?: () => void
}

export const AddLink = ({
  text,
  initialQuery = '',
  onLinked,
  onClose,
  onLinkHeading,
  onChange,
  onRemoveLink,
}: Props) => {
  const [query, setQuery] = useState(initialQuery)
  const [submitted, setSubmitted] = useState(false)
  const [linkError, setLinkError] = useState(false)

  const isEdit = initialQuery !== ''

  const submit = () => {
    if (query) {
      let transformedLink = query

      if (!query.startsWith('http://') && !query.startsWith('https://') && !query.startsWith('mailto:')) {
        transformedLink = `http://${query}`
      }

      onLinked({ link: transformedLink, text })
    }
  }

  const onSubmit = () => {
    setSubmitted(true)

    if (isEdit && !query) {
      onLinked({ text })
      onClose()
    } else if (checkUrl(query)) {
      submit()
      onClose()
    } else {
      setLinkError(true)
    }
  }

  const handleQueryChange = (event: any) => {
    const newLink = (event.target as HTMLInputElement).value
    setQuery(newLink)

    if (submitted) {
      setLinkError(!checkUrl(newLink))
    }
  }

  const onKeyDown = (event: any) => {
    if (event.key === 'Escape') {
      onClose()
    } else if (event.key === 'Enter' || event.keyCode === 13) {
      event.preventDefault()
      onSubmit()
    }
  }

  return (
    <>
      <div className={cx(Styles.modalBody)}>
        <input
          key="modalText"
          className={cx(Styles.input, Styles.textInput)}
          type="text"
          value={text}
          onChange={(e) => {
            onChange(e.target.value)
          }}
          onKeyDown={onKeyDown}
          placeholder="Enter display text"
        />

        <QueryOrLink
          query={query}
          linkError={linkError}
          onChange={handleQueryChange}
          onKeyDown={onKeyDown}
          onSubmit={onSubmit}
        />

        <div className={cx(Styles.mentionPrompt, { [Styles.mentionPromptError]: linkError })}>
          <MentionPrompt
            title="Link to..."
            showTitleWithNoResults={false}
            inputQuery={query}
            suggestRecentlyReadArticle={false}
            allowCreateNewFile={false}
            onSelect={({ type, key, url, title }: SelectionAttributes) => {
              let link = ''

              if (type === 'Article' && key) {
                link = getArticleUrl(key)
              } else if (type === 'Folder') {
                link = getFolderUrl(url)
              } else {
                link = url
              }

              onLinked({ link, text: text === '' ? title : text })
              onClose()
            }}
            onCancel={onClose}
            dropdownWidth="100%"
            overrideContainerClassname={Styles.mentionPromptContainer}
          />
        </div>
      </div>

      <div className={cx(Styles.modalFooter)}>
        <Button size="md" variant="tertiary" onPress={onLinkHeading}>
          Link to a heading in this doc
        </Button>
        {onRemoveLink && (
          <Button size="md" variant="tertiary" onPress={onRemoveLink} frontIcon={Unlink}>
            Remove
          </Button>
        )}
      </div>
    </>
  )
}
